/**
 * Decision 0101 (S7): honest close labels. A `done` item says what it delivered (verified, report delivered, no data) and
 * a cancelled one why (won't do, duplicate, impossible, superseded, blocked by something outside), so blocked or no-data
 * work never reads as plain "done" and delivered work never as "cancelled". Shared by the web, the agents' tools and the
 * turn file.
 */
import { itemOf, runOf } from "./state"
import type { CloseResult, Item, ProjectState } from "./types"

export const CLOSE_RESULT_WORDS: Readonly<Record<CloseResult, string>> = {
  verified: "verified",
  report_delivered: "report delivered",
  no_data: "no data",
}

/** What a done item delivered: its close's `result`, else its accepted run's; null when nobody said (or not done). */
export const itemResult = (s: ProjectState, t: Item): CloseResult | null => {
  if (t.status !== "done") return null
  if (t.result) return t.result
  const r = t.lastRunId ? runOf(s, t.lastRunId) : undefined
  return r?.outcome?._tag === "done" ? (r.outcome.result ?? null) : null
}

/** "won't do", "duplicate of DIC-3", "impossible: …", "superseded by DIC-9", "blocked: App Store write key". */
export const resolutionWords = (s: ProjectState, r: Item["resolution"], max = 200): string | null => {
  if (!r) return null
  const ref = (id: string) => itemOf(s, id as never)?.ref ?? "another item"
  const clip = (x: string) => (x.length > max ? `${x.slice(0, max - 1)}…` : x)
  switch (r._tag) {
    case "wont_do":
      return "won't do"
    case "duplicate":
      return `duplicate of ${ref(r.of)}`
    case "impossible":
      return r.reason ? `impossible: ${clip(r.reason)}` : "impossible"
    case "superseded":
      return `superseded by ${ref(r.by)}`
    case "blocked_external":
      return `blocked: ${clip(r.what)}`
  }
}

/** The close label of a closed item: "done · report delivered", "done", "cancelled · blocked: …"; null when open. */
export const closeLabel = (s: ProjectState, t: Item): string | null => {
  if (t.status === "done") {
    const r = itemResult(s, t)
    return r ? `done · ${CLOSE_RESULT_WORDS[r]}` : "done"
  }
  if (t.status === "cancelled") {
    const w = resolutionWords(s, t.resolution)
    return w ? `cancelled · ${w}` : "cancelled"
  }
  return null
}
